import React from 'react';
import { Input, Modal, Spin, Button, Select, Tooltip } from 'antd';
import { FixedSizeList as List } from 'react-window';
import { Plus, X, ChevronLeft, ChevronRight, Store, Check } from 'lucide-react';
import { EmptyState } from '../page';
import { useCalendarStoresModal } from './calendarStoresModal.controller';

// Virtualized row: store name + RIF and an add / remove toggle.
const Row = React.memo(({ index, style, data }) => {
    const { stores, selectedStores, toggleStore } = data;
    const store = stores[index];
    const selected = selectedStores?.includes(store?._id);
    return (
        <div style={style} className={`flex flex-row items-center justify-between gap-3 px-4 border-b border-surface-100 ${selected ? 'bg-primary-50/60' : ''}`}>
            <div className="flex items-center gap-3 min-w-0">
                <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[14px] ${
                        selected ? 'bg-primary-100 text-primary-700' : 'bg-surface-100 text-surface-500'
                    }`}>
                    {selected ? <Check /> : <Store />}
                </span>
                <div className="min-w-0">
                    <p className="m-0 text-sm font-medium text-surface-900 truncate">{store.name}</p>
                    <p className="m-0 text-xs text-surface-500 tabular-nums">{store.rif}</p>
                </div>
            </div>
            <Tooltip title={selected ? 'Quitar del día' : 'Agregar al día'}>
                <Button
                    size="small"
                    type={selected ? 'text' : 'default'}
                    danger={selected}
                    icon={selected ? <X /> : <Plus />}
                    aria-label={selected ? 'Quitar del día' : 'Agregar al día'}
                    onClick={() => toggleStore(store?._id)}
                />
            </Tooltip>
        </div>
    );
});
Row.displayName = 'CalendarStoreRow';

export default function CalendarStoresModal({ isOpen, setIsOpen, calendarStores, day, user, stores, createCalendar }) {
    const {
        loading,
        search,
        currentPage,
        setCurrentPage,
        totalStores,
        totalPages,
        selectedStores,
        toggleStore,
        isSearching,
        onSearch,
        clients,
        tags,
        selectedClientId,
        setSelectedClientId,
        selectedTagId,
        setSelectedTagId,
        loadingFilters,
        loadingTagItems,
        filteredStores
    } = useCalendarStoresModal({ isOpen, calendarStores, stores });

    return (
        <Modal
            title="Pisos de Venta"
            open={isOpen}
            cancelText="Cancelar"
            okText="Guardar"
            onCancel={() => setIsOpen(false)}
            onOk={() => createCalendar(user, day, selectedStores)}
            width={600}>
            <div className="flex flex-col">
                <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div>
                        <span className="mb-1.5 block text-[13px] font-medium text-surface-700">Filtrar por Cliente</span>
                        <Select
                            className="w-full"
                            placeholder="Todos los clientes"
                            allowClear
                            loading={loadingFilters}
                            value={selectedClientId || undefined}
                            onChange={(value) => setSelectedClientId(value || '')}
                            options={clients.map((client) => ({
                                label: client?.name ?? 'Sin nombre',
                                value: client?._id?.toString() ?? ''
                            }))}
                            showSearch
                            optionFilterProp="label"
                        />
                    </div>
                    <div>
                        <span className="mb-1.5 block text-[13px] font-medium text-surface-700">Filtrar por Tag</span>
                        <Select
                            className="w-full"
                            placeholder="Todas las etiquetas"
                            allowClear
                            loading={loadingFilters || loadingTagItems}
                            value={selectedTagId || undefined}
                            onChange={(value) => setSelectedTagId(value || '')}
                            options={tags.map((tag) => ({
                                label: tag?.name ?? 'Sin nombre',
                                value: tag?._id?.toString() ?? ''
                            }))}
                            showSearch
                            optionFilterProp="label"
                        />
                    </div>
                </div>
                <Input.Search
                    value={search}
                    onChange={(e) => onSearch(e.target.value)}
                    placeholder="Buscar piso de venta por nombre, RIF o cliente"
                    loading={isSearching}
                />

                <div className="mt-3 flex items-center justify-between text-xs text-surface-500">
                    <span className="tabular-nums">
                        {search !== '' ? (
                            <>Mostrando {filteredStores?.length ?? 0} resultados de búsqueda</>
                        ) : (
                            <>
                                Mostrando {filteredStores?.length ?? 0} de {totalStores} pisos de venta
                                {totalPages > 1 && ` (Página ${currentPage} de ${totalPages})`}
                            </>
                        )}
                    </span>
                    <span className="font-medium text-primary-700 tabular-nums">{selectedStores?.length || 0} seleccionados</span>
                </div>

                <div className="mt-2 h-96 overflow-hidden rounded-lg border border-surface-200">
                    {loading ? (
                        <div className="flex justify-center items-center h-full">
                            <Spin />
                        </div>
                    ) : filteredStores?.length ? (
                        <List
                            height={382} // h-96 minus the 1px borders
                            itemCount={filteredStores?.length ?? 0}
                            itemSize={70} // Approximate height of each row
                            width="100%"
                            itemData={{ stores: filteredStores ?? [], selectedStores, toggleStore }}>
                            {Row}
                        </List>
                    ) : (
                        <EmptyState icon={<Store />} title="Sin pisos de venta" description="Ajusta la búsqueda o los filtros." className="h-full" />
                    )}
                </div>

                {/* Pagination Controls */}
                {search === '' && totalPages > 1 && (
                    <div className="mt-3 flex justify-between items-center">
                        <Button size="small" icon={<ChevronLeft />} disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                            Anterior
                        </Button>

                        <span className="text-sm text-surface-600 tabular-nums">
                            Página {currentPage} de {totalPages}
                        </span>

                        <Button
                            size="small"
                            icon={<ChevronRight />}
                            iconPosition="end"
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(currentPage + 1)}>
                            Siguiente
                        </Button>
                    </div>
                )}
            </div>
        </Modal>
    );
}
